import { type MutableRefObject, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { createReference } from "@medplum/core";
import type { Practitioner, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { type UseFormReturnType } from "@mantine/form";

import {
  LABOR_AMEND_CANCEL_BLOCKED_MESSAGE,
  LABOR_AMEND_CANCEL_BLOCKED_TITLE,
  LABOR_AMEND_ENCOUNTER_SAVE_FAILED_MESSAGE,
  LABOR_AMEND_ENCOUNTER_SAVE_FAILED_TITLE,
  LABOR_AMEND_FLOWSHEET_FLUSH_FAILED_TITLE,
  LABOR_AMEND_NOT_READY_MESSAGE,
  LABOR_AMEND_NOT_READY_TITLE,
  LABOR_AMEND_POST_EFFECTS_FAILED_MESSAGE,
  LABOR_AMEND_POST_EFFECTS_FAILED_TITLE,
  LABOR_AMEND_POST_EFFECTS_LOST_MESSAGE,
  LABOR_AMEND_POST_EFFECTS_LOST_TITLE,
  LABOR_AMEND_PROVENANCE_FAILED_MESSAGE,
  LABOR_AMEND_PROVENANCE_FAILED_TITLE,
  LABOR_AMEND_VALIDATION_ERROR_MESSAGE,
  LABOR_AMEND_VALIDATION_ERROR_TITLE,
} from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import { useBirthObservations } from "@/encounters/components/EncounterLabor/hooks/useBirthObservations";
import type { CreateLaborEncounterAmendCompletionProvenanceResult } from "@/encounters/components/EncounterLabor/types/labor-amend-completion-provenance";
import type { LaborAmendSignAndLockResult } from "@/encounters/components/EncounterLabor/types/labor-amend-sign-and-lock";
import { shouldSkipEncounterAmendmentSave } from "@/encounters/components/EncounterLabor/utils/labor-amend-encounter-save-verification";
import {
  clearAllLaborAmendSessionState,
  clearLaborAmendPostEffectsPendingFromSession,
} from "@/encounters/components/EncounterLabor/utils/labor-amend-session-state";
import { executeLaborAmendSignAndLock } from "@/encounters/components/EncounterLabor/utils/labor-amend-sign-and-lock";
import { useEncounterChildSave } from "@/encounters/contexts/EncounterChildSaveContext";
import { useLaborAmendFlowsheetPersist } from "@/encounters/contexts/LaborAmendFlowsheetPersistContext";
import { EncounterAmendmentFormType } from "@/encounters/zod-schemas/encounter-answers";
import { toast } from "@/utils/notifications";

interface UseLaborAmendmentActionsParams {
  form: UseFormReturnType<EncounterAmendmentFormType>;
  clientId: string;
  encounterId: string;
  questionnaireResponse: QuestionnaireResponse | undefined;
  isSignAndLockReady: boolean;
  onSignAndLock: (values: EncounterAmendmentFormType) => Promise<QuestionnaireResponse>;
  onStripPreviewLaborStages?: () => void;
}

export function useLaborAmendmentActions({
  form,
  clientId,
  encounterId,
  questionnaireResponse,
  isSignAndLockReady,
  onSignAndLock,
  onStripPreviewLaborStages,
}: UseLaborAmendmentActionsParams) {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const practitionerReference =
    profile?.resourceType === "Practitioner" && profile.id
      ? createReference(profile as Practitioner)
      : undefined;
  const [isSigningAndLocking, setIsSigningAndLocking] = useState(false);
  const isSignAndLockInFlightRef = useRef(false);
  const encounterAmendmentSavedEncounterIdRef = useRef<string | undefined>(
    shouldSkipEncounterAmendmentSave({
      encounterId,
      questionnaireResponse,
      inMemorySavedEncounterId: undefined,
    })
      ? encounterId
      : undefined,
  );

  useEffect(() => {
    if (encounterAmendmentSavedEncounterIdRef.current === encounterId) {
      return;
    }

    encounterAmendmentSavedEncounterIdRef.current = shouldSkipEncounterAmendmentSave({
      encounterId,
      questionnaireResponse,
      inMemorySavedEncounterId: undefined,
    })
      ? encounterId
      : undefined;
  }, [encounterId, questionnaireResponse]);
  const { clearOptimisticBirth } = useBirthObservations(encounterId, clientId);
  const childSave = useEncounterChildSave();
  const flowsheetPersist = useLaborAmendFlowsheetPersist();
  const {
    commitAmendLocalPreviews,
    discardFlowsheetPersistQueue,
    hasCommittedFlowsheetToServer,
    setSignAndLockInProgress,
  } = flowsheetPersist;

  const handleCancel = () => {
    if (hasCommittedFlowsheetToServer) {
      toast({
        title: LABOR_AMEND_CANCEL_BLOCKED_TITLE,
        message: LABOR_AMEND_CANCEL_BLOCKED_MESSAGE,
        type: "warning",
        autoClose: false,
      });
      return;
    }

    discardFlowsheetPersistQueue();
    clearOptimisticBirth();
    clearAllLaborAmendSessionState(encounterId);
    navigate(`/clients/${clientId}/encounters/${encounterId}`, {
      preventScrollReset: true,
    });
  };

  const handleSignAndLockClick = async () => {
    if (isSignAndLockInFlightRef.current) {
      return;
    }

    const validation = form.validate();
    if (validation.hasErrors) {
      notifyLaborAmendSignAndLockFailure({ ok: false, reason: "validation_error" });
      return;
    }

    if (!isSignAndLockReady) {
      notifyLaborAmendSignAndLockFailure({ ok: false, reason: "not_ready" });
      return;
    }

    const values = form.getValues();
    isSignAndLockInFlightRef.current = true;
    setIsSigningAndLocking(true);
    setSignAndLockInProgress(true);
    try {
      const signAndLockResult = await executeLaborAmendSignAndLock({
        medplum,
        clientId,
        encounterId,
        questionnaireResponse,
        formValues: values,
        inMemorySavedEncounterId: encounterAmendmentSavedEncounterIdRef.current,
        practitionerReference,
        saveEncounterAmendment: onSignAndLock,
        flowsheetPersist,
      });

      if (!signAndLockResult.ok) {
        markEncounterAmendmentSavedAfterPartialFailure(
          encounterId,
          signAndLockResult,
          encounterAmendmentSavedEncounterIdRef,
        );
        notifyLaborAmendSignAndLockFailure(signAndLockResult);
        return;
      }

      if (!signAndLockResult.encounterSaveSkipped) {
        encounterAmendmentSavedEncounterIdRef.current = encounterId;
      }

      if (!signAndLockResult.provenanceResult.ok) {
        notifyLaborAmendProvenanceFailure(signAndLockResult.provenanceResult, {
          encounterId,
          encounterQuestionnaireResponseVersionId:
            signAndLockResult.savedQuestionnaireResponse.meta?.versionId,
        });
      }

      await childSave?.saveAll({ status: "amended" });

      commitAmendLocalPreviews();
      onStripPreviewLaborStages?.();
      clearLaborAmendPostEffectsPendingFromSession(encounterId);
      clearAllLaborAmendSessionState(encounterId);
      navigate(`/clients/${clientId}/encounters/${encounterId}`, {
        state: { statusChanged: true },
        preventScrollReset: true,
      });
    } finally {
      isSignAndLockInFlightRef.current = false;
      setIsSigningAndLocking(false);
      setSignAndLockInProgress(false);
    }
  };

  return {
    handleCancel,
    handleSignAndLockClick,
    isSigningAndLocking,
    isSignAndLockReady,
    isCancelDisabled: isSigningAndLocking || hasCommittedFlowsheetToServer,
  };
}

type FailedLaborAmendSignAndLockResult = Extract<LaborAmendSignAndLockResult, { ok: false }>;
type FailedLaborAmendProvenanceResult = Extract<
  CreateLaborEncounterAmendCompletionProvenanceResult,
  { ok: false }
>;

interface NotifyLaborAmendProvenanceFailureContext {
  encounterId: string;
  encounterQuestionnaireResponseVersionId?: string;
}

type LaborAmendSignAndLockNotificationFailure =
  | FailedLaborAmendSignAndLockResult
  | { ok: false; reason: "validation_error" }
  | { ok: false; reason: "not_ready" };

/**
 * Records in-memory encounter save progress when Sign & Lock partially succeeded.
 */
function markEncounterAmendmentSavedAfterPartialFailure(
  encounterId: string,
  signAndLockResult: FailedLaborAmendSignAndLockResult,
  encounterAmendmentSavedEncounterIdRef: MutableRefObject<string | undefined>,
): void {
  if (
    signAndLockResult.savedQuestionnaireResponse &&
    signAndLockResult.reason !== "encounter_save_failed"
  ) {
    encounterAmendmentSavedEncounterIdRef.current = encounterId;
  }
}

/**
 * Shows user-facing feedback for a failed or blocked labor amend Sign & Lock attempt.
 */
function notifyLaborAmendSignAndLockFailure(
  failure: LaborAmendSignAndLockNotificationFailure,
): void {
  switch (failure.reason) {
    case "validation_error":
      toast({
        title: LABOR_AMEND_VALIDATION_ERROR_TITLE,
        message: LABOR_AMEND_VALIDATION_ERROR_MESSAGE,
        type: "error",
      });
      return;
    case "not_ready":
      toast({
        title: LABOR_AMEND_NOT_READY_TITLE,
        message: LABOR_AMEND_NOT_READY_MESSAGE,
        type: "error",
        autoClose: false,
      });
      return;
    case "encounter_save_failed":
      toast({
        title: LABOR_AMEND_ENCOUNTER_SAVE_FAILED_TITLE,
        message: failure.errorMessage ?? LABOR_AMEND_ENCOUNTER_SAVE_FAILED_MESSAGE,
        type: "error",
        autoClose: false,
      });
      console.error("Error saving encounter amendment:", failure.errorMessage);
      return;
    case "flowsheet_flush_failed":
      toast({
        title: LABOR_AMEND_FLOWSHEET_FLUSH_FAILED_TITLE,
        message: failure.errorMessage ?? "",
        type: "error",
        autoClose: false,
      });
      return;
    case "post_effects_lost":
      toast({
        title: LABOR_AMEND_POST_EFFECTS_LOST_TITLE,
        message: LABOR_AMEND_POST_EFFECTS_LOST_MESSAGE,
        type: "error",
        autoClose: false,
      });
      return;
    case "post_effects_failed":
      toast({
        title: LABOR_AMEND_POST_EFFECTS_FAILED_TITLE,
        message: failure.errorMessage ?? LABOR_AMEND_POST_EFFECTS_FAILED_MESSAGE,
        type: "error",
        autoClose: false,
      });
      console.error("Error running post-amendment flowsheet side effects:", failure.errorMessage);
  }
}

/**
 * Warns when amend save succeeded but completion recording failed.
 * Logs operational details to the console; toast uses a generic user message.
 */
function notifyLaborAmendProvenanceFailure(
  provenanceResult: FailedLaborAmendProvenanceResult,
  context: NotifyLaborAmendProvenanceFailureContext,
): void {
  toast({
    title: LABOR_AMEND_PROVENANCE_FAILED_TITLE,
    message: LABOR_AMEND_PROVENANCE_FAILED_MESSAGE,
    type: "warning",
    autoClose: false,
  });
  console.error("Labor amend provenance was not recorded", {
    encounterId: context.encounterId,
    encounterQuestionnaireResponseVersionId: context.encounterQuestionnaireResponseVersionId,
    reason: provenanceResult.reason,
    details: provenanceResult.details,
  });
}
